import React, { useCallback, useState } from 'react';

import {
  Card,
  CardClose,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { useOnMapPanelExit } from '../Map/MapPanel/useOnMapPanelExit';
import { EventList } from './EventList';
import { DetectorList } from './Detectors/DetectorList';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { openPopupOnSecondaryScreen, PopupType } from '../Popup';
import { PiArrowSquareOut } from 'react-icons/pi';
import { useEventsContext } from './EventsContext';

interface EventsProps {
  className: string;
}

export const Events = React.memo(({ className }: EventsProps) => {
  const { messages } = useLocalization();
  const onExit = useOnMapPanelExit();
  const [tab, setTab] = useState<'events' | 'eventdetector'>('events');
  const permissions = usePermissions();
  const { date } = useEventsContext();

  const handleTabChange = useCallback((value: string) => {
    setTab(value as 'events' | 'eventdetector');
  }, []);

  const handlePopout = useCallback(() => {
    openPopupOnSecondaryScreen(PopupType.Events, {
      dateFrom: date?.from?.toISOString() || '',
      dateTo: date?.to?.toISOString() || '',
    });
  }, [date]);

  return (
    <Card className={cn('flex flex-col w-[384px]', className)}>
      <CardHeader variant="tinted">
        <CardClose onClick={onExit} />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              id="open-events-popup-button"
              className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={handlePopout}
            >
              <PiArrowSquareOut className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Open in new window</TooltipContent>
        </Tooltip>
        <div className="space-y-2">
          <CardTitle className="text-lg font-medium text-muted-foreground">
            {messages.eventsListTitle}
          </CardTitle>
          <Tabs
            value={tab}
            onValueChange={handleTabChange}
            className="w-full space-y-6 pt-4 h-full flex flex-col"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="events">
                {messages.eventsTabEvents}
              </TabsTrigger>

              <TabsTrigger
                id="events_detector_tab_button"
                value="eventdetector"
                disabled={!permissions.eventdetector}
              >
                {messages.eventsTabDetectors}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      {tab === 'events' && <EventList />}
      {tab === 'eventdetector' && <DetectorList />}
    </Card>
  );
});

Events.displayName = 'Events';
